import React, { useState } from "react";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { API_BASE_URL } from "../../config";
import { FiUser, FiMail, FiPhone, FiLock, FiMapPin } from "react-icons/fi";

const MySwal = withReactContent(Swal);

const RegistrationBox: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    agree: false,
  });
  const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | "none">("none");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const evaluatePasswordStrength = (password: string): "weak" | "medium" | "strong" => {
    if (password.length < 8 || password.includes(" ")) return "weak";
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const hasLetter = /[A-Za-z]/.test(password);
    if (password.length >= 12 && hasNumber && hasSpecial && hasLetter) return "strong";
    if (password.length >= 8 && hasNumber && (hasSpecial || hasLetter)) return "medium";
    return "weak";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === "checkbox";
    const checked = isCheckbox && (e.target as HTMLInputElement).checked;

    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: numericValue }));
      return;
    }

    if (name === "pincode") {
      const numericValue = value.replace(/\D/g, "").slice(0, 6);
      setFormData((prev) => ({ ...prev, pincode: numericValue }));
      return;
    }

    if (name === "password") {
      const cleanedPassword = value.replace(/\s/g, "").slice(0, 15);
      setFormData((prev) => ({ ...prev, password: cleanedPassword }));
      setPasswordStrength(cleanedPassword ? evaluatePasswordStrength(cleanedPassword) : "none");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: isCheckbox ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { fullName, email, phone, password, address, city, state, pincode, agree } = formData;

    if (!fullName || !email || !phone || !password || !address || !city || !state || !pincode) {
      MySwal.fire({
        icon: 'error',
        title: 'Oops...',
        text: "Please fill out all the fields.",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      });
      setIsSubmitting(false);
      return;
    }

    if (phone.length !== 10) {
      MySwal.fire({
        icon: 'error',
        title: 'Oops...',
        text: "Phone number must be exactly 10 digits.",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      });
      setIsSubmitting(false);
      return;
    }

    if (pincode.length !== 6) {
      MySwal.fire({
        icon: 'error',
        title: 'Oops...',
        text: "Pincode must be exactly 6 digits.",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      });
      setIsSubmitting(false);
      return;
    }

    const passwordRegex = /^(?=.*[0-9])(?=.*[^A-Za-z0-9])([^\s]{8,15})$/;
    if (!passwordRegex.test(password)) {
      MySwal.fire({
        icon: 'error',
        title: 'Oops...',
        text: "Password must be 8-15 characters, include a number, a special character, and have no spaces.",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      });
      setIsSubmitting(false);
      return;
    }

    if (!agree) {
      MySwal.fire({
        icon: 'error',
        title: 'Oops...',
        text: "You must agree to the terms and privacy policy.",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      });
      setIsSubmitting(false);
      return;
    }

    const payload = {
      username: fullName,
      email,
      password,
      address,
      city,
      state,
      pincode,
      mobile: phone
    };

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let resultText = await response.text();
      let result;

      try {
        result = JSON.parse(resultText);
      } catch (e) {
        result = { message: resultText }; // fallback if not JSON
      }

      if (!response.ok || result.success === false) {
        // Choose icon based on message content
        const icon = result.message?.includes("already confirmed") ? "info" : "error";

        MySwal.fire({
          icon,
          title: icon === "info" ? "FYI" : "Oops...",
          text: result.message || "Something went wrong.",
           customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
        });
        return;
      }

      MySwal.fire({
        icon: "success",
        title: "Please Verify Your Email!",
        html: "A confirmation link has been sent to your email.<br><b>Click it to activate your account.</b>",
        confirmButtonText: "Go to Login",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      }).then(() => {
        window.location.href = "/login";
      });

    } catch (err) {
      console.error("Error during signup:", err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Please check the email address or contact support.",
         customClass: {
    confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400'
  },
  buttonsStyling: false
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex flex-col">
      {/* Space for topbar (mt-16) and centered content */}
      <div className="flex-grow flex justify-center items-start px-4 py-8 mt-16">
        <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
            <h2 className="text-2xl font-bold mb-1">Register for Quizora</h2>
            <p className="text-blue-100 text-sm">Create your account to participate in monthly contests</p>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Full Name & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Phone & Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiPhone className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      inputMode="numeric"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiLock className="text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-10"
                      onKeyDown={(e) => e.key === " " && e.preventDefault()}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {passwordStrength !== "none" && (
                    <div className="mt-2">
                      <div className="flex gap-2 mb-1">
                        <div
                          className={`h-1 flex-1 rounded ${passwordStrength === "weak" ? "bg-red-500" : "bg-green-500"}`}
                        ></div>
                        <div
                          className={`h-1 flex-1 rounded ${passwordStrength === "weak" ? "bg-gray-200" : passwordStrength === "medium" ? "bg-yellow-500" : "bg-green-500"}`}
                        ></div>
                        <div
                          className={`h-1 flex-1 rounded ${passwordStrength === "strong" ? "bg-green-500" : "bg-gray-200"}`}
                        ></div>
                      </div>
                      <p
                        className={`text-xs ${passwordStrength === "weak"
                          ? "text-red-600"
                          : passwordStrength === "medium"
                            ? "text-yellow-600"
                            : "text-green-600"
                          }`}
                      >
                        {passwordStrength === "weak"
                          ? "Weak - Add numbers & special characters"
                          : passwordStrength === "medium"
                            ? "Medium - Could be stronger"
                            : "Strong password"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                <div className="relative">
                  <div className="absolute top-3 left-3">
                    <FiMapPin className="text-gray-400" />
                  </div>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Your full address"
                    className="pl-10 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                    required
                  ></textarea>
                </div>
              </div>

              {/* City / State / Pincode */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    inputMode="numeric"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="6-digit code"
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
              </div>

              {/* Agreement */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="agree"
                    name="agree"
                    checked={formData.agree}
                    onChange={handleChange}
                    className="h-4 w-4 accent-indigo-600 rounded focus:ring-indigo-500"
                    required
                  />
                </div>
                <label htmlFor="agree" className="ml-3 text-sm text-gray-700">
                  I agree to the{" "}
                  <a href="/terms" className="text-indigo-600 hover:underline">
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a href="#" className="text-indigo-600 hover:underline">
                    Privacy Policy
                  </a>
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    "Register Now"
                  )}
                </button>
              </div>

              {/* Login Link */}
              <div className="text-center text-sm text-gray-600">
                Already have an account?{" "}
                <a href="/login" className="font-medium text-indigo-600 hover:underline">
                  Log in
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationBox;